import React, { useEffect, useRef, useState } from "react";

import classNames from "classnames";
import dynamic from "next/dynamic";
import ReactMarkdown from "react-markdown";

import { ContentData } from "data/product/products";

import { removeCharactersFromTextNodes } from "~/components/BTSInProductVideoPage/utils/removeCharactersFromTextNodes";

import * as styles from "./VideoDesc.css";

const ReactQuill = dynamic(() => import("react-quill"), { ssr: false });

export const VideoDesc = ({
  currentVideo,
  indexOfCurrentVideo,
}: {
  currentVideo: ContentData;
  indexOfCurrentVideo: number;
}): React.ReactElement => {
  const descriptionRef = useRef<any>(null);
  const [showMore, setShowMore] = useState(false);
  const contentNotes = currentVideo.contentNotes || currentVideo.description || "";
  const isLessonNotesInHtml = contentNotes.includes("<p>");
  const [truncatedText, setTruncatedText] = useState({
    content: contentNotes,
    truncated: false,
  });

  const handleTextExpansionClick = () => {
    if (!truncatedText.truncated) return;
    setShowMore((prev) => {
      if (!prev) scrollToDescription();
      return !prev;
    });
  };

  const showMoreHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMore(false);
  };

  const showLessHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMore(true);
    scrollToDescription();
  };

  const scrollToDescription = () => {
    if (descriptionRef.current) {
      const descriptionTop = descriptionRef.current.offsetTop;
      const averageDescriptionHeight = 200;
      const visibleHeight = window.innerHeight;
      const scrollPosition = descriptionTop - (visibleHeight - averageDescriptionHeight) / 1.4;
      window.scrollTo({ top: scrollPosition });
    }
  };

  useEffect(() => {
    const DESKTOP_CHARACTER_COUNT_LIMIT = 350;
    if (contentNotes?.length || 0 <= DESKTOP_CHARACTER_COUNT_LIMIT) {
      setTruncatedText({ content: contentNotes, truncated: false });
      setShowMore(false);
      return;
    }

    setShowMore(true);
    const truncatedText = contentNotes.slice(0, DESKTOP_CHARACTER_COUNT_LIMIT).trim();
    setTruncatedText({
      content: truncatedText,
      truncated: true,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVideo, indexOfCurrentVideo]);

  return (
    <>
      <h2 className={styles.videoHeading}>{indexOfCurrentVideo + 1 + ". " + currentVideo.title}</h2>
      <div
        ref={descriptionRef}
        onClick={handleTextExpansionClick}
        className={classNames(
          styles.videoDescBlock,
          truncatedText.truncated && styles.videoDescBlockHover,
        )}
      >
        <div className={styles.descriptionWrap}>
          <div
            ref={(node) => removeCharactersFromTextNodes(node, showMore)}
            className={classNames(styles.videoDesc)}
          >
            {!isLessonNotesInHtml ? (
              <ReactMarkdown linkTarget="_blank">
                {showMore && truncatedText.truncated
                  ? truncatedText.content + " ..."
                  : contentNotes}
              </ReactMarkdown>
            ) : (
              <div
                className={styles.videoDescHtml}
                dangerouslySetInnerHTML={{
                  __html:
                    showMore && truncatedText.truncated
                      ? truncatedText.content + " ..."
                      : contentNotes,
                }}
              />
            )}
          </div>

          {showMore && (
            <span onClick={showMoreHandler} className={classNames(styles.descriptionMoreDeco)}>
              Show More
            </span>
          )}

          {!showMore && truncatedText.truncated && (
            <span onClick={showLessHandler} className={styles.descriptionMoreDeco}>
              Show Less
            </span>
          )}
        </div>
      </div>
    </>
  );
};
